import { Card, rankOf, suitOf } from "./cards.ts";

/**
 * Hand scores are a single integer: higher always beats lower, equal means a tie.
 *   score = category << 20 | five 4-bit rank slots (most significant first)
 */
export const enum HandCategory {
  HighCard = 0,
  Pair = 1,
  TwoPair = 2,
  ThreeOfAKind = 3,
  Straight = 4,
  Flush = 5,
  FullHouse = 6,
  FourOfAKind = 7,
  StraightFlush = 8,
}

const CATEGORY_NAMES = [
  "High Card",
  "Pair",
  "Two Pair",
  "Three of a Kind",
  "Straight",
  "Flush",
  "Full House",
  "Four of a Kind",
  "Straight Flush",
];

export const categoryOf = (score: number): number => score >> 20;

export function handName(score: number): string {
  const cat = categoryOf(score);
  if (cat === HandCategory.StraightFlush && ((score >> 16) & 0xf) === 12) return "Royal Flush";
  return CATEGORY_NAMES[cat]!;
}

function pack(category: number, ranks: number[]): number {
  let s = category << 20;
  for (let i = 0; i < 5; i++) s |= (ranks[i] ?? 0) << (16 - 4 * i);
  return s;
}

export function evaluate5(cards: readonly Card[]): number {
  const flush = cards.every((c) => suitOf(c) === suitOf(cards[0]!));
  const counts = new Array<number>(13).fill(0);
  for (const c of cards) counts[rankOf(c)]!++;

  // groups sorted by count desc, then rank desc
  const groups: Array<[count: number, rank: number]> = [];
  for (let r = 12; r >= 0; r--) if (counts[r]! > 0) groups.push([counts[r]!, r]);
  groups.sort((a, b) => b[0] - a[0] || b[1] - a[1]);

  const ordered = groups.flatMap(([n, r]) => new Array<number>(n).fill(r));
  const shape = groups.map((g) => g[0]).join("");

  let straightHigh = -1;
  if (groups.length === 5) {
    const hi = groups[0]![1];
    const lo = groups[4]![1];
    if (hi - lo === 4) straightHigh = hi;
    else if (hi === 12 && groups[1]![1] === 3 && lo === 0) straightHigh = 3; // wheel: A-2-3-4-5
  }

  if (straightHigh >= 0 && flush) return pack(HandCategory.StraightFlush, [straightHigh]);
  if (shape === "41") return pack(HandCategory.FourOfAKind, ordered);
  if (shape === "32") return pack(HandCategory.FullHouse, ordered);
  if (flush) return pack(HandCategory.Flush, ordered);
  if (straightHigh >= 0) return pack(HandCategory.Straight, [straightHigh]);
  if (shape === "311") return pack(HandCategory.ThreeOfAKind, ordered);
  if (shape === "221") return pack(HandCategory.TwoPair, ordered);
  if (shape === "2111") return pack(HandCategory.Pair, ordered);
  return pack(HandCategory.HighCard, ordered);
}

/** Best 5-card hand out of 5 to 7 cards. */
export function evaluate(cards: readonly Card[]): number {
  const n = cards.length;
  if (n < 5 || n > 7) throw new Error(`evaluate needs 5-7 cards, got ${n}`);
  if (n === 5) return evaluate5(cards);
  let best = -1;
  const pick: Card[] = [];
  const go = (start: number): void => {
    if (pick.length === 5) {
      best = Math.max(best, evaluate5(pick));
      return;
    }
    for (let i = start; i < n; i++) {
      pick.push(cards[i]!);
      go(i + 1);
      pick.pop();
    }
  };
  go(0);
  return best;
}
